import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import classnames from 'classnames';
import {
  Button,
  FormGroup,
  Input,
  Label,
  UncontrolledTooltip,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import IntegrationsButton from './Button';
import DesktopSyncDownload from './DesktopSyncDownload';

import { APIintegrationCallMethod } from '../../api/Integrations';

import { slugify } from '../../helpers';
import {
  userIsAdmin,
  userIsManager,
} from '../../helpers/Auth';
import { addFlashMessage } from '../../helpers/FlashMessages';

const IntegrationsIntegration = ({
  integration,
  disconnecting,
  handleDisconnect,
}) => {
  const [apiKey, setApiKey] = useState(integration?.api_key || '');
  const [authTokenFull, setAuthTokenFull] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [processing, setProcessing] = useState(false);

  const intl = useIntl();

  if (!integration) {
    return null;
  }

  const handleChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    if (name === 'api-key') {
      setApiKey(value);
    }
  };

  const handleSave = (e) => {
    const { name } = e.target;

    setProcessing(true);

    if (name === 'save-api-key') {
      APIintegrationCallMethod(integration.id, 'saveApiKey', { api_key: apiKey })
        .then(() => {
          addFlashMessage({
            type: 'success',
            text: intl.formatMessage({ defaultMessage: 'Your changes have been saved' }),
          });
        })
        .catch(() => {
          addFlashMessage({
            type: 'danger',
            text: intl.formatMessage({ defaultMessage: 'Something went wrong' }),
          });
        })
        .finally(() => {
          setProcessing(false);
        });
    }
  };

  const handleCopy = () => {
    setCodeCopied(true);
    setTimeout(() => {
      setCodeCopied(false);
    }, 3000);

    addFlashMessage({
      type: 'success',
      text: intl.formatMessage({ defaultMessage: 'Integration key copied' }),
    });
  };

  const handleShowAuthToken = (e) => {
    e.preventDefault();
    setAuthTokenFull(true);
  };

  return (
    <div className="tab-content settings-tab-content">
      <div className="tab-pane active">
        <div className="tab-content-wrapper">
          <h4>{integration.name}</h4>
          <p>{integration.description}</p>
          {integration.authenticate_api_key && (
            <FormGroup className="api-key-form">
              {(userIsAdmin() || userIsManager()) ? (
                <>
                  <Label for="api-key">
                    {intl.formatMessage({ defaultMessage: 'API key' })}
                  </Label>
                  <div>
                    <Input type="text" id="api-key" name="api-key" onChange={handleChange} value={apiKey} />
                    <Button type="button" name="save-api-key" disabled={processing} onClick={handleSave}>
                      {processing ? (
                        intl.formatMessage({ defaultMessage: 'Saving...' })
                      ) : (
                        intl.formatMessage({ defaultMessage: 'Save' })
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {!apiKey && (
                    <>
                      <p>
                        <strong>{intl.formatMessage({ defaultMessage: 'Not connected!' })}</strong>
                      </p>
                      <p>
                        {intl.formatMessage({ defaultMessage: 'Only administrators and managers can manage this connection.' })}
                      </p>
                    </>
                  )}
                </>
              )}
            </FormGroup>
          )}
          {integration.authentication_token && (
            <FormGroup>
              <Label for="authentication-token">
                {intl.formatMessage({ defaultMessage: 'Integration key' })}
                <CopyToClipboard
                  text={integration.authentication_token}
                  options={{
                    format: 'text/plain',
                  }}
                  onCopy={handleCopy}
                >
                  <a href="#copy" onClick={(e) => { e.preventDefault(); }} id="copy-authentication-token">
                    {codeCopied ? (
                      <i className="far fa-check ml-2" />
                    ) : (
                      <i className="far fa-copy ml-2" />
                    )}
                  </a>
                </CopyToClipboard>
                <UncontrolledTooltip fade={false} target="copy-authentication-token">
                  {intl.formatMessage({ defaultMessage: 'Copy integration key' })}
                </UncontrolledTooltip>
              </Label>
              <div id="authentication-token" className={classnames('authentication-token', { 'show-short': !authTokenFull })}>
                <code>
                  {integration.authentication_token}
                </code>
                <a href="#show" onClick={handleShowAuthToken}>show</a>
              </div>
            </FormGroup>
          )}
          {(integration.account && integration.account.length > 0) && (
            <div className="integration-account">
              <ul>
                {integration.account.map(line => (
                  <li key={slugify(line.label)}>
                    {line.type === 'button' && (
                      <Button tag="a" href={line.value} target="_blank">
                        {line.label}
                      </Button>
                    )}
                    {!line.type && (
                      <>
                        <strong>{line.label}</strong><br />
                        {line.value}
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(!integration.authenticate_api_key && integration.id !== 'desktop-sync') && (
            <IntegrationsButton
              integration={integration}
              disconnecting={disconnecting}
              handleDisconnect={handleDisconnect}
            />
          )}
          {integration.id === 'desktop-sync' && (
            <DesktopSyncDownload integration={integration} />
          )}
        </div>
      </div>
    </div>
  );
};

IntegrationsIntegration.propTypes = {
  integration: PropTypes.object,
  disconnecting: PropTypes.bool.isRequired,
  handleDisconnect: PropTypes.func.isRequired,
};

IntegrationsIntegration.defaultProps = {
  integration: null,
};

export default IntegrationsIntegration;
